import type { EngagementKind } from "@mvk/shared";
import { appConfig } from "../config";
import { outboxService } from "./outboxService";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `eng-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const recent = new Set<string>();

function dedupeKey(eventId: string, kind: string, targetId: string) {
  return `${eventId}:${kind}:${targetId}`;
}

export const engagementService = {
  async track(input: {
    eventId: string;
    kind: EngagementKind;
    targetType: string;
    targetId: string;
    companyId?: string;
    meta?: Record<string, unknown>;
  }): Promise<void> {
    if (!appConfig.featureFlags.enableAnalytics) return;
    if (!input.eventId || !input.targetId) return;

    const key = dedupeKey(input.eventId, input.kind, input.targetId);
    if (recent.has(key)) return;
    recent.add(key);
    window.setTimeout(() => recent.delete(key), 8_000);

    await outboxService.enqueueEngagement(input.eventId, [
      {
        idempotencyKey: uuid(),
        kind: input.kind,
        targetType: input.targetType,
        targetId: input.targetId,
        companyId: input.companyId,
        meta: input.meta,
        occurredAt: new Date().toISOString(),
      },
    ]);
  },

  trackCompanyView(eventId: string, companyId: string) {
    return this.track({
      eventId,
      kind: "company_view",
      targetType: "company",
      targetId: companyId,
      companyId,
    });
  },

  trackProductView(eventId: string, productId: string, companyId: string) {
    return this.track({
      eventId,
      kind: "product_view",
      targetType: "product",
      targetId: productId,
      companyId,
    });
  },

  trackSearch(eventId: string, query: string) {
    const q = query.trim();
    if (!q) return Promise.resolve();
    return this.track({
      eventId,
      kind: "search_query",
      targetType: "search",
      targetId: q.slice(0, 80),
      meta: { query: q },
    });
  },
};
